import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Plus } from 'lucide-react';
import { useSession, useIsInternalStaff } from '@/hooks/useSession';
import { useSites, useAccounts, useUpdateSite, useDeleteSite } from '../hooks';
import { SitesFilters } from '../components/SitesFilters';
import { SitesTable } from '../components/SitesTable';
import { SiteFormModal, CreateSiteModal } from '../components/modals';
import { exportSitesToCSV } from '../utils/csvExport';
import { exportSitesToExcel } from '../utils/excelExport';
import type { Site, SitesFilters as SitesFiltersType, UpdateSiteInput } from '../types';
import { DEFAULT_VISIBLE_COLUMNS, type SiteColumnKey } from '../components/ColumnVisibilityToggle';

const COLUMNS_STORAGE_KEY = 'atek_site_columns';

function loadVisibleColumns(): SiteColumnKey[] {
  try {
    const stored = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return DEFAULT_VISIBLE_COLUMNS;
}

export default function SitesPage() {
  const { t } = useTranslation('sites');

  // Session for admin level checks
  const { user: sessionUser } = useSession();
  const isFullAdmin = !!(sessionUser?.postgres_roles?.includes('full_admin') || sessionUser?.postgres_roles?.includes('director'));
  const isAtekUser = useIsInternalStaff();
  const canDelete = isFullAdmin;

  // Filter state
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [accountId, setAccountId] = useState('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sortKey, setSortKey] = useState<SitesFiltersType['sortKey']>('name');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);

  // Per-column filters
  const [nameFilter, setNameFilter] = useState('');
  const [debouncedNameFilter, setDebouncedNameFilter] = useState('');
  const [addressFilter, setAddressFilter] = useState('');
  const [debouncedAddressFilter, setDebouncedAddressFilter] = useState('');
  const [tagsFilter, setTagsFilter] = useState('');
  const [debouncedTagsFilter, setDebouncedTagsFilter] = useState('');
  const [locationsFilter, setLocationsFilter] = useState('');
  const [debouncedLocationsFilter, setDebouncedLocationsFilter] = useState('');

  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<SiteColumnKey[]>(loadVisibleColumns);

  // Modal state
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Debounce per-column filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedNameFilter(nameFilter);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [nameFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedAddressFilter(addressFilter);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [addressFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTagsFilter(tagsFilter);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [tagsFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedLocationsFilter(locationsFilter);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [locationsFilter]);

  // Save column visibility to localStorage
  const handleVisibleColumnsChange = (columns: SiteColumnKey[]) => {
    setVisibleColumns(columns);
    localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(columns));
  };

  // Build filters object
  const parsedTags = debouncedTagsFilter
    ? debouncedTagsFilter.split(',').map((t) => t.trim()).filter(Boolean)
    : undefined;

  const filters: SitesFiltersType = {
    search: debouncedSearch || undefined,
    accountId: accountId !== 'all' ? accountId : undefined,
    sortKey,
    sortDirection,
    pageNumber: page,
    pageLength: pageSize,
    name: debouncedNameFilter || undefined,
    address: debouncedAddressFilter || undefined,
    tags: parsedTags && parsedTags.length > 0 ? parsedTags : undefined,
    locations: debouncedLocationsFilter || undefined,
  };

  // Queries
  const sitesQuery = useSites(filters);
  const accountsQuery = useAccounts();

  // Mutations
  const updateSiteMutation = useUpdateSite();
  const deleteSiteMutation = useDeleteSite();

  const sites = sitesQuery.data?.sites || [];
  const total = sitesQuery.data?.total || 0;
  const accounts = accountsQuery.data || [];

  // Check if any filters are active
  const hasActiveFilters = !!(
    search ||
    accountId !== 'all' ||
    nameFilter ||
    addressFilter ||
    tagsFilter ||
    locationsFilter
  );

  // Handlers
  const handleSort = (key: SitesFiltersType['sortKey']) => {
    if (key === sortKey) {
      setSortDirection(sortDirection === 1 ? -1 : 1);
    } else {
      setSortKey(key);
      setSortDirection(1);
    }
    setPage(0);
  };

  const handleAccountChange = (value: string) => {
    setAccountId(value);
    setPage(0);
  };

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setPage(0);
  };

  const handleUpdateSite = async (data: UpdateSiteInput) => {
    if (!editingSite) return;
    const siteId = editingSite.id || editingSite._id || editingSite.mongoSiteId;
    if (!siteId) {
      toast.error('Cannot update site: missing site ID');
      return;
    }

    await updateSiteMutation.mutateAsync({
      siteId,
      data,
    });
    setEditingSite(null);
  };

  const handleDeleteSite = async (siteId: string) => {
    if (!siteId) {
      toast.error('Cannot delete site: missing site ID');
      return;
    }
    await deleteSiteMutation.mutateAsync(siteId);
    setEditingSite(null);
  };

  const handleExportCSV = () => {
    try {
      exportSitesToCSV(sites, 'sites');
      toast.success(`Exported ${sites.length} sites to CSV`);
    } catch {
      toast.error('Failed to export sites');
    }
  };

  const handleExportExcel = () => {
    try {
      exportSitesToExcel(sites);
      toast.success(`Exported ${sites.length} sites to Excel`);
    } catch {
      toast.error('Failed to export sites');
    }
  };

  const handleClearFilters = () => {
    setSearch('');
    setAccountId('all');
    setNameFilter('');
    setAddressFilter('');
    setTagsFilter('');
    setLocationsFilter('');
    setPage(0);
  };

  return (
    <div className="space-y-5">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="page-title">
            {t('page.title', 'Sites')} <small>{t('page.subtitle', 'Manage sites and locations')}</small>
          </h1>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-1" />
          {t('toolbar.create', 'Create')}
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between gap-4">
        <SitesFilters
          search={search}
          onSearchChange={setSearch}
          accountId={accountId}
          onAccountChange={handleAccountChange}
          pageSize={pageSize}
          onPageSizeChange={handlePageSizeChange}
          accounts={accounts}
          isLoadingAccounts={accountsQuery.isLoading}
          onExport={handleExportCSV}
          onExportExcel={handleExportExcel}
          nameFilter={nameFilter}
          onNameFilterChange={setNameFilter}
          addressFilter={addressFilter}
          onAddressFilterChange={setAddressFilter}
          tagsFilter={tagsFilter}
          onTagsFilterChange={setTagsFilter}
          locationsFilter={locationsFilter}
          onLocationsFilterChange={setLocationsFilter}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={handleClearFilters}
          visibleColumns={visibleColumns}
          onVisibleColumnsChange={handleVisibleColumnsChange}
        />
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {total} {t('toolbar.count', 'site')}{total !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      {sitesQuery.isLoading ? (
        <Card>
          <CardContent className="p-0">
            <div className="space-y-2 p-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : sitesQuery.error ? (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-red-500">Error loading sites. Please try again.</p>
          </CardContent>
        </Card>
      ) : (
        <SitesTable
          sites={sites}
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
          onEditClick={setEditingSite}
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          showAccount={isAtekUser}
          visibleColumns={visibleColumns}
        />
      )}

      {/* Edit Modal */}
      <SiteFormModal
        open={!!editingSite}
        onOpenChange={(open) => {
          if (!open) setEditingSite(null);
        }}
        site={editingSite}
        onSubmit={handleUpdateSite}
        onDelete={handleDeleteSite}
        isSubmitting={updateSiteMutation.isPending}
        isDeleting={deleteSiteMutation.isPending}
        canDelete={canDelete}
      />

      {/* Create Modal */}
      <CreateSiteModal open={showCreateModal} onOpenChange={setShowCreateModal} />
    </div>
  );
}
